import React, { useState, useEffect, useCallback } from 'react';
import { getClientPaymentInfo, savePaymentInfo, updatePaymentInfo, deletePaymentInfo } from '../../services/payments';
import './PaymentInfoTab.css';

export default function PaymentInfoTab({ clientId }) {
  const [paymentInfo, setPaymentInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masked, setMasked] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    card_type: '',
    card_number: '',
    cardholder_name: '',
    expiry_date: '',
    cvv: '',
    billing_address: '',
    is_primary: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadPaymentInfo = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClientPaymentInfo(clientId);
      setPaymentInfo(data);
    } catch (error) {
      console.error('Error loading payment info:', error);
      // Don't show error if table doesn't exist yet
      if (!error.message.includes('relation') && !error.message.includes('does not exist')) {
        alert('Failed to load payment info: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadPaymentInfo();
  }, [loadPaymentInfo]);

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      card_type: payment.card_type || '',
      card_number: payment.card_number || '',
      cardholder_name: payment.cardholder_name || '',
      expiry_date: payment.expiry_date || '',
      cvv: payment.cvv || '',
      billing_address: payment.billing_address || '',
      is_primary: payment.is_primary || false,
    });
    setShowAddForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (paymentId) => {
    if (window.confirm('Are you sure you want to delete this payment method?')) {
      try {
        await deletePaymentInfo(paymentId);
        setSuccess('Payment method deleted successfully!');
        await loadPaymentInfo();
        setTimeout(() => setSuccess(''), 2000);
      } catch (error) {
        setError('Failed to delete payment method: ' + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      if (!formData.card_number || !formData.cardholder_name) {
        setError('Card number and cardholder name are required');
        setSaving(false);
        return;
      }

      if (editingPayment) {
        // Update existing payment
        const updateData = {
          card_type: formData.card_type,
          card_number: formData.card_number,
          cardholder_name: formData.cardholder_name,
          expiry_date: formData.expiry_date,
          cvv: formData.cvv,
          billing_address: formData.billing_address,
          is_primary: formData.is_primary,
        };
        await updatePaymentInfo(editingPayment.id, updateData);
        setSuccess('Payment information updated successfully!');
      } else {
        // Create new payment
        const paymentData = {
          ...formData,
          client_id: clientId,
        };
        await savePaymentInfo(paymentData);
        setSuccess('Payment information saved successfully!');
      }

      // Reset form and reload
      setFormData({
        card_type: '',
        card_number: '',
        cardholder_name: '',
        expiry_date: '',
        cvv: '',
        billing_address: '',
        is_primary: false,
      });
      setEditingPayment(null);
      setShowAddForm(false);
      await loadPaymentInfo(); // Immediate UI feedback
    } catch (error) {
      setError(error.message || 'Failed to save payment information');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setEditingPayment(null);
    setError('');
    setSuccess('');
    setFormData({
      card_type: '',
      card_number: '',
      cardholder_name: '',
      expiry_date: '',
      cvv: '',
      billing_address: '',
      is_primary: false,
    });
  };

  const maskCardNumber = (cardNumber) => {
    if (!cardNumber) return '';
    const cleaned = cardNumber.replace(/\s/g, '');
    if (cleaned.length < 4) return cardNumber;
    return '**** **** **** ' + cleaned.slice(-4);
  };

  const maskCVV = (cvv) => {
    if (!cvv) return '';
    return '***';
  };

  if (loading) {
    return (
      <div className="payment-info-tab">
        <div className="loading-state">Loading payment information...</div>
      </div>
    );
  }

  return (
    <div className="payment-info-tab">
      <div className="tab-header">
        <h2>Payment Information</h2>
        <div className="header-actions">
          <button
            className={`btn-mask ${masked ? 'masked' : ''}`}
            onClick={() => setMasked(!masked)}
          >
            {masked ? 'Show Card Info' : 'Mask Card Info'}
          </button>
          <button
            className="btn-add-payment"
            onClick={() => setShowAddForm(true)}
          >
            + Add Payment Info
          </button>
        </div>
      </div>

      {paymentInfo.length === 0 ? (
        <div className="empty-state">
          <p>No payment information on file for this client.</p>
        </div>
      ) : (
        <div className="payment-info-list">
          {paymentInfo.map((payment) => (
            <div key={payment.id} className="payment-card">
              <div className="payment-header">
                <h3>{payment.card_type || 'Payment Method'}</h3>
                <div className="payment-header-actions">
                  {payment.is_primary && (
                    <span className="primary-badge">Primary</span>
                  )}
                  <button
                    className="btn-edit"
                    onClick={() => handleEdit(payment)}
                    title="Edit payment method"
                  >
                    Edit
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(payment.id)}
                    title="Delete payment method"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="payment-details">
                <div className="detail-row">
                  <span className="detail-label">Card Number:</span>
                  <span className="detail-value">
                    {masked ? maskCardNumber(payment.card_number) : payment.card_number}
                  </span>
                </div>

                {payment.cardholder_name && (
                  <div className="detail-row">
                    <span className="detail-label">Cardholder Name:</span>
                    <span className="detail-value">{payment.cardholder_name}</span>
                  </div>
                )}

                {payment.expiry_date && (
                  <div className="detail-row">
                    <span className="detail-label">Expiry Date:</span>
                    <span className="detail-value">{payment.expiry_date}</span>
                  </div>
                )}

                {payment.cvv && (
                  <div className="detail-row">
                    <span className="detail-label">CVV:</span>
                    <span className="detail-value">
                      {masked ? maskCVV(payment.cvv) : payment.cvv}
                    </span>
                  </div>
                )}

                {payment.billing_address && (
                  <div className="detail-row">
                    <span className="detail-label">Billing Address:</span>
                    <span className="detail-value">{payment.billing_address}</span>
                  </div>
                )}

                {payment.created_at && (
                  <div className="detail-row">
                    <span className="detail-label">Added:</span>
                    <span className="detail-value">
                      {new Date(payment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="info-note">
        <p>
          <strong>Note:</strong> This section is for storing payment information. 
          Payment processing should be handled through a secure payment gateway.
        </p>
      </div>

      {/* Add/Edit Payment Info Modal */}
      {showAddForm && (
        <div className="modal-overlay" onClick={handleCloseForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPayment ? 'Edit Payment Information' : 'Add Payment Information'}</h2>
              <button className="btn-close" onClick={handleCloseForm}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="payment-form">
              {error && (
                <div className="error-message">{error}</div>
              )}
              {success && (
                <div className="success-message">{success}</div>
              )}

              <div className="form-group">
                <label>Card Type</label>
                <select
                  value={formData.card_type}
                  onChange={(e) => setFormData({ ...formData, card_type: e.target.value })}
                >
                  <option value="">Select card type</option>
                  <option value="Visa">Visa</option>
                  <option value="Mastercard">Mastercard</option>
                  <option value="American Express">American Express</option>
                  <option value="Discover">Discover</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Card Number *</label>
                <input
                  type="text"
                  required
                  value={formData.card_number}
                  onChange={(e) => setFormData({ ...formData, card_number: e.target.value })}
                  placeholder="1234 5678 9012 3456"
                  maxLength="19"
                />
              </div>

              <div className="form-group">
                <label>Cardholder Name *</label>
                <input
                  type="text"
                  required
                  value={formData.cardholder_name}
                  onChange={(e) => setFormData({ ...formData, cardholder_name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="text"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    placeholder="MM/YY"
                    maxLength="5"
                  />
                </div>
                <div className="form-group">
                  <label>CVV</label>
                  <input
                    type="text"
                    value={formData.cvv}
                    onChange={(e) => setFormData({ ...formData, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    placeholder="123"
                    maxLength="4"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Billing Address</label>
                <input
                  type="text"
                  value={formData.billing_address}
                  onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                  placeholder="123 Main St, City, State ZIP"
                />
              </div>

              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="isPrimary"
                    checked={formData.is_primary}
                    onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                  />
                  <label htmlFor="isPrimary" className="checkbox-label">
                    Set as primary payment method
                  </label>
                </div>
                <small className="form-hint">
                  Only one payment method can be marked as primary. Selecting this will unset any other primary payment method.
                </small>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseForm}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingPayment ? 'Update Payment Info' : 'Save Payment Info'}
                </button>
              </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
